import FooterPrivacy from 'src/views/auth/components/baseTemplate/FooterPrivacy';
import { RadioButton } from 'src/components/ui/radioButton';
import { useTranslation } from 'react-i18next';
import { VEHICLEINFO_VIN_ERROR } from 'src/localization/resouceKeys';
import EligibilityResults, {
    EligibilityResultsSkeleton,
    IProgramEligibilityResult,
    parseEligibilityResults,
} from './results';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { matchAccountNumber, matchVINWithIOLetter } from 'src/utils/validators';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import {
    ProgramEligibilitySearchType,
    ProgramEligibilityService,
} from 'src/services/program-eligibility.service';
import './styles.scss';

const INITIAL_REQUEST_ID = 0;
const EMPTY_RESULT_COUNT = 0;
const INPUT_START_INDEX = 0;
const ACCOUNT_NUMBER_MAX_LENGTH = 14;
const VIN_MAX_LENGTH = 17;
const NO_ELVIS_DATA_MESSAGE = 'No Elvis data Found for given params.';

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const getApiErrorMessage = (data: unknown): string => {
    if (typeof data === 'string') return data.trim();
    if (!isRecord(data)) return '';
    const nestedMessage = isRecord(data.error) ? data.error.message : undefined;

    if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
        return nestedMessage;
    }
    return typeof data.message === 'string' ? data.message.trim() : '';
};

const ProgramEligibility = () => {
    const { t } = useTranslation();
    const label = (key: string) => t(`ProgramEligibility.${key}`);
    const [searchType, setSearchType] =
        useState<ProgramEligibilitySearchType>('ACCOUNT');
    const [searchValue, setSearchValue] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState('');
    const [showErrorNotification, setShowErrorNotification] = useState(false);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<IProgramEligibilityResult[] | null>(
        null,
    );
    const activeService = useRef<ProgramEligibilityService | null>(null);
    const requestId = useRef(INITIAL_REQUEST_ID);
    const inputRef = useRef<HTMLInputElement>(null);
    const isAccount = searchType === 'ACCOUNT';
    const maxLength = isAccount ? ACCOUNT_NUMBER_MAX_LENGTH : VIN_MAX_LENGTH;
    const isSearchValid = isAccount
        ? matchAccountNumber(searchValue.trim())
        : matchVINWithIOLetter(searchValue.trim());
    const validationError =
        isSubmitted && searchValue.trim() !== '' && !isSearchValid;
    const noMatchingAccount = error.trim() === NO_ELVIS_DATA_MESSAGE;

    const showSearchError = (message: string) => {
        setError(message);
        setShowErrorNotification(message.trim() !== NO_ELVIS_DATA_MESSAGE);
    };

    useEffect(
        () => () => {
            requestId.current += 1;
            activeService.current?.abort();
        },
        [],
    );

    const reset = () => {
        requestId.current += 1;
        activeService.current?.abort();
        activeService.current = null;
        setSearchValue('');
        setResults(null);
        setError('');
        setShowErrorNotification(false);
        setIsSubmitted(false);
        setLoading(false);
    };

    const search = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (loading) return;
        const value = searchValue.trim();
        setIsSubmitted(true);
        if (!isSearchValid) {
            inputRef.current?.focus();
            return;
        }

        const currentRequest = ++requestId.current;
        const service = new ProgramEligibilityService();
        activeService.current = service;
        setSearchValue(value);
        setLoading(true);
        setResults(null);
        setError('');
        setShowErrorNotification(false);
        try {
            const response = await service.search(
                searchType,
                isAccount ? value : value.toUpperCase(),
            );
            if (requestId.current !== currentRequest) return;
            try {
                setResults(parseEligibilityResults(response.data));
            } catch {
                showSearchError(
                    getApiErrorMessage(response.data) || label('ResponseError'),
                );
            }
        } catch (error) {
            if (requestId.current === currentRequest) {
                const response = isRecord(error) ? error.response : undefined;
                const message = isRecord(response)
                    ? getApiErrorMessage(response.data)
                    : '';
                showSearchError(message || label('Error'));
            }
        } finally {
            if (requestId.current === currentRequest) {
                setLoading(false);
                activeService.current = null;
            }
        }
    };

    return (
        <div className='program-eligibility'>
            <header className='program-eligibility__header'>
                {showErrorNotification && (
                    <div role='alert'>
                        <Notification
                            content={error}
                            type={NotificationType.ERROR}
                            visibility={showErrorNotification}
                            setVisibility={setShowErrorNotification}
                        />
                    </div>
                )}
                <h1>{label('Title')}</h1>
                <p>{label('Subtitle')}</p>
            </header>
            <div className='program-eligibility__content'>
                <section
                    className='program-eligibility__search'
                    aria-labelledby='eligibility-search-title'
                >
                    <h2 id='eligibility-search-title'>
                        {label('SearchCriteria')}
                    </h2>
                    <p className='program-eligibility__instructions'>
                        {label('SearchInstructions')}
                    </p>
                    <form onSubmit={search} noValidate>
                        <fieldset>
                            <legend>{label('SearchBy')}</legend>
                            <RadioButton
                                name='eligibility-search-type'
                                options={['ACCOUNT', 'VIN']}
                                labels={[label('AccountNumber'), label('VIN')]}
                                selectedOption={searchType}
                                onSelect={(
                                    value: ProgramEligibilitySearchType,
                                ) => {
                                    reset();
                                    setSearchType(value);
                                }}
                            />
                        </fieldset>
                        <div className='program-eligibility__search-row'>
                            <div
                                className={`c-form-field program-eligibility__input ${
                                    validationError ? 'is-error' : ''
                                }`}
                            >
                                <label htmlFor='eligibility-search-value'>
                                    {label(isAccount ? 'AccountNumber' : 'VIN')}
                                </label>
                                <input
                                    ref={inputRef}
                                    id='eligibility-search-value'
                                    className='c-input'
                                    type='text'
                                    inputMode={isAccount ? 'numeric' : 'text'}
                                    maxLength={maxLength}
                                    autoComplete='off'
                                    spellCheck={false}
                                    placeholder={label(
                                        isAccount
                                            ? 'AccountPlaceholder'
                                            : 'VINPlaceholder',
                                    )}
                                    value={searchValue}
                                    disabled={loading}
                                    aria-invalid={validationError}
                                    aria-describedby='eligibility-search-hint'
                                    onChange={(event) => {
                                        const value =
                                            event.target.value.replace(
                                                isAccount
                                                    ? /\D/g
                                                    : /[^a-zA-Z0-9]/g,
                                                '',
                                            );
                                        const normalizedValue = isAccount
                                            ? value
                                            : value.toUpperCase();
                                        setSearchValue(
                                            normalizedValue.slice(
                                                INPUT_START_INDEX,
                                                maxLength,
                                            ),
                                        );
                                    }}
                                />
                            </div>
                            <div className='program-eligibility__actions'>
                                <button
                                    className='c-btn c-btn--small'
                                    type='submit'
                                    disabled={
                                        loading || searchValue.trim() === ''
                                    }
                                >
                                    <span className='c-btn__text'>
                                        {label('Search')}
                                    </span>
                                </button>
                                <button
                                    className='c-btn c-btn--small c-btn--secondary'
                                    type='button'
                                    onClick={() => {
                                        reset();
                                        inputRef.current?.focus();
                                    }}
                                >
                                    <span className='c-btn__text'>
                                        {label('Clear')}
                                    </span>
                                </button>
                            </div>
                        </div>
                        <p
                            id='eligibility-search-hint'
                            className={`program-eligibility__hint ${
                                validationError
                                    ? 'program-eligibility__error'
                                    : ''
                            }`}
                            role={validationError ? 'alert' : undefined}
                        >
                            {isAccount
                                ? label(
                                      validationError
                                          ? 'AccountError'
                                          : 'AccountHint',
                                  )
                                : validationError
                                ? t(VEHICLEINFO_VIN_ERROR)
                                : label('VINHint')}
                        </p>
                    </form>
                </section>
                <div
                    className='program-eligibility__results'
                    aria-live='polite'
                    aria-busy={loading}
                >
                    {loading && (
                        <div
                            className='program-eligibility__skeleton'
                            role='status'
                            aria-label={label('Loading')}
                        >
                            <EligibilityResultsSkeleton />
                        </div>
                    )}
                    {!loading && !error && results === null && (
                        <p>{label('EmptyState')}</p>
                    )}
                    {noMatchingAccount && (
                        <p role='status'>{label('NoMatchingAccount')}</p>
                    )}
                    {results?.length === EMPTY_RESULT_COUNT && (
                        <p role='status'>{label('NoResults')}</p>
                    )}
                    {results?.map((result, index) => (
                        <EligibilityResults
                            key={`${result.accountNumber}-${index}`}
                            result={result}
                        />
                    ))}
                </div>
            </div>
            <footer className='program-eligibility__footer'>
                <FooterPrivacy />
            </footer>
        </div>
    );
};

export default ProgramEligibility;
